import { create } from 'zustand';

/**
 * Which Pass the paywall opens highlighted. Mirrors Apple's
 * `PassPaywallSheet.Highlight` ({ pro, designer }).
 *
 * The highlight is chosen by the *feature* that was tapped, NOT re-derived on
 * the client: Designer-exclusive features (output_style, album_full_template,
 * white_label per EntitlementFeatureMap.php) open 'designer' so the Designer
 * disclosure is pre-expanded; all other Pro perks (incl. model_selection,
 * moved Designer → Pro 2026-05-27) open 'pro'. Opening the wrong highlight
 * would upsell a Pass that doesn't grant the tapped feature.
 */
export type PassPaywallHighlight = 'pro' | 'designer';

/**
 * App-wide presenter for the Pass paywall — the monetization-pivot replacement
 * for the legacy subscription `LimitPaywallDialog` on *feature* gates.
 *
 * Mirrors `useLimitPaywallStore` + Apple's `PassPaywallPresenter`: the dialog is
 * mounted once at the root and any deep render-settings gate just calls
 * `open(highlight)`. Subscriptions are being hidden from the UI, so a locked
 * Pro/Designer feature must upsell the Pass, never the old plans screen — even
 * though a subscription still structurally unlocks the same feature server-side
 * (the API unions subscription + Pass into the `is_feature_*` / `has_*` flags).
 * The store only owns *which* Pass to highlight; all copy is server-driven from
 * `GET /v1/passes`.
 */
interface PassPaywallState {
  highlight: PassPaywallHighlight | null;
  open: (highlight: PassPaywallHighlight) => void;
  close: () => void;
}

export const usePassPaywallStore = create<PassPaywallState>((set) => ({
  highlight: null,
  open: (highlight) => set({ highlight }),
  close: () => set({ highlight: null }),
}));
